import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Select,
  MenuItem,
  Chip,
  Paper,
  Tooltip,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import type { DevMessageFile } from '@/lib/impedance';

// ─── Pair item ────────────────────────────────────────────────────────────────

export interface FilePairItem {
  baseline: DevMessageFile;
  specimen: DevMessageFile;
  included: boolean;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface FilePairPreviewProps {
  parsedFiles: DevMessageFile[];
  pairs: FilePairItem[];
  onTogglePair: (index: number) => void;
  onSwapPair: (index: number) => void;
  onReassign: (
    pairIndex: number,
    slot: 'baseline' | 'specimen',
    file: DevMessageFile,
  ) => void;
  unpairedFiles: DevMessageFile[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function frequenciesMatch(a: DevMessageFile, b: DevMessageFile): boolean {
  const fa = a.impedanceResult.frequency_Hz;
  const fb = b.impedanceResult.frequency_Hz;
  if (fa.length !== fb.length) return false;
  return fa.every((v, i) => Math.abs(v - fb[i]) < 0.001);
}

function getPairStatus(
  pair: FilePairItem,
): { label: string; color: 'success' | 'warning' } {
  if (!frequenciesMatch(pair.baseline, pair.specimen)) {
    return { label: '⚠ Freq mismatch', color: 'warning' };
  }
  if (pair.baseline.uid !== pair.specimen.uid) {
    return { label: '⚠ Different device', color: 'warning' };
  }
  return { label: '✓ Valid', color: 'success' };
}

function formatTimestamp(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Component ────────────────────────────────────────────────────────────────

export function FilePairPreview({
  parsedFiles,
  pairs,
  onTogglePair,
  onSwapPair,
  onReassign,
  unpairedFiles,
}: FilePairPreviewProps) {
  return (
    <Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Baseline File</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Specimen File</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Device UID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>BL Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>SP Timestamp</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Swap</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pairs.map((pair, idx) => {
              const status = getPairStatus(pair);
              return (
                <TableRow
                  key={`${pair.baseline.filename}-${pair.specimen.filename}`}
                  sx={{ opacity: pair.included ? 1 : 0.45 }}
                >
                  {/* Include checkbox */}
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={pair.included}
                      onChange={() => onTogglePair(idx)}
                      size="small"
                      inputProps={{ 'aria-label': `Include pair ${idx + 1}` }}
                    />
                  </TableCell>

                  {/* Pair number */}
                  <TableCell>{idx + 1}</TableCell>

                  {/* Baseline file select */}
                  <TableCell>
                    <Select
                      size="small"
                      value={pair.baseline.filename}
                      sx={{ minWidth: 110, fontSize: '0.8rem' }}
                      onChange={(e) => {
                        const newFile = parsedFiles.find(
                          (f) => f.filename === e.target.value,
                        );
                        if (newFile) onReassign(idx, 'baseline', newFile);
                      }}
                    >
                      {parsedFiles.map((f) => (
                        <MenuItem key={f.filename} value={f.filename}>
                          {f.filename}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>

                  {/* Specimen file select */}
                  <TableCell>
                    <Select
                      size="small"
                      value={pair.specimen.filename}
                      sx={{ minWidth: 110, fontSize: '0.8rem' }}
                      onChange={(e) => {
                        const newFile = parsedFiles.find(
                          (f) => f.filename === e.target.value,
                        );
                        if (newFile) onReassign(idx, 'specimen', newFile);
                      }}
                    >
                      {parsedFiles.map((f) => (
                        <MenuItem key={f.filename} value={f.filename}>
                          {f.filename}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>

                  {/* Device UID */}
                  <TableCell>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {pair.baseline.uid.slice(0, 10)}…
                    </Typography>
                  </TableCell>

                  {/* BL Timestamp */}
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                    {formatTimestamp(pair.baseline.timestampMs)}
                  </TableCell>

                  {/* SP Timestamp */}
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                    {formatTimestamp(pair.specimen.timestampMs)}
                  </TableCell>

                  {/* Status chip */}
                  <TableCell>
                    <Chip
                      label={status.label}
                      color={status.color}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  {/* Swap button */}
                  <TableCell>
                    <Tooltip title="Swap baseline ↔ specimen">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => onSwapPair(idx)}
                          aria-label={`Swap pair ${idx + 1}`}
                        >
                          <SwapHorizIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Unpaired files warning */}
      {unpairedFiles.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          <strong>Unpaired file{unpairedFiles.length > 1 ? 's' : ''} — will be skipped:</strong>{' '}
          {unpairedFiles.map((f) => f.filename).join(', ')}
        </Alert>
      )}
    </Box>
  );
}
